/**
 * Unified Profile Store
 * PRD-18: Trading Context Profiles (Enhanced)
 *
 * SINGLE SOURCE OF TRUTH for all profile UI state.
 * Replaces: use-profile-store.ts, use-trading-profile-store.ts
 *
 * Legacy stores are thin re-export shims pointing here.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { ProfileNotification } from "@/types/unified-profiles";

// =============================================
// TYPES
// =============================================

interface UnifiedProfileStore {
  // Active trading profile (string UUID from v2 trading profiles)
  activeProfileId: string | null;
  /** Alias for activeProfileId — legacy name from use-trading-profile-store */
  activeTradingProfileId: string | null;

  // Selected legacy numeric profile ID (v1 broker profiles)
  selectedProfileId: number;

  // UI State
  isCommandCenterOpen: boolean;
  isSwitchingProfile: boolean;
  isCreatingProfile: boolean;

  // Cross-Profile Notifications (in-memory for real-time)
  localNotifications: ProfileNotification[];
  showNotificationBadge: boolean;

  // Quick Filters
  showOnlyWithOpenTrades: boolean;
  searchQuery: string;

  // View preferences
  viewMode: "cards" | "list";
  sortBy: "name" | "pnl" | "trades" | "lastActivity";
  sortOrder: "asc" | "desc";

  // Cross-Profile Trades Panel
  isTradesPanelOpen: boolean;
  selectedPositionIds: string[];

  // Actions - Profile
  setActiveProfileId: (id: string | null) => void;
  /** Alias for setActiveProfileId — legacy name from use-trading-profile-store */
  setActiveTradingProfileId: (id: string | null) => void;
  setSelectedProfileId: (id: number) => void;

  // Actions - UI
  openCommandCenter: () => void;
  closeCommandCenter: () => void;
  toggleCommandCenter: () => void;
  setIsSwitchingProfile: (switching: boolean) => void;
  setIsCreatingProfile: (creating: boolean) => void;

  // Actions - Notifications
  addLocalNotification: (notification: ProfileNotification) => void;
  removeLocalNotification: (id: string) => void;
  clearLocalNotifications: () => void;
  setShowNotificationBadge: (show: boolean) => void;

  // Actions - Filters
  setShowOnlyWithOpenTrades: (show: boolean) => void;
  setSearchQuery: (query: string) => void;

  // Actions - View
  setViewMode: (mode: "cards" | "list") => void;
  setSortBy: (sortBy: "name" | "pnl" | "trades" | "lastActivity") => void;
  setSortOrder: (order: "asc" | "desc") => void;

  // Actions - Trades Panel
  openTradesPanel: () => void;
  closeTradesPanel: () => void;
  toggleTradesPanel: () => void;
  selectPosition: (positionId: string) => void;
  deselectPosition: (positionId: string) => void;
  togglePositionSelection: (positionId: string) => void;
  selectAllPositions: (positionIds: string[]) => void;
  clearPositionSelection: () => void;

  // Legacy profile management UI toggle
  isManagingProfiles: boolean;
  setIsManagingProfiles: (managing: boolean) => void;
}

// =============================================
// STORE
// =============================================

export const useUnifiedProfileStore = create<UnifiedProfileStore>()(
  persist(
    (set, get) => ({
      // Initial state
      activeProfileId: null,
      activeTradingProfileId: null,
      selectedProfileId: 0,
      isCommandCenterOpen: false,
      isSwitchingProfile: false,
      isCreatingProfile: false,
      localNotifications: [],
      showNotificationBadge: false,
      showOnlyWithOpenTrades: false,
      searchQuery: "",
      viewMode: "cards",
      sortBy: "name",
      sortOrder: "asc",
      isTradesPanelOpen: false,
      selectedPositionIds: [],
      isManagingProfiles: false,

      // Actions - Profile (synced aliases)
      setActiveProfileId: (id) => set({ activeProfileId: id, activeTradingProfileId: id }),
      setActiveTradingProfileId: (id) => set({ activeProfileId: id, activeTradingProfileId: id }),
      setSelectedProfileId: (id) => set({ selectedProfileId: id }),

      // Actions - UI
      openCommandCenter: () => set({ isCommandCenterOpen: true, isManagingProfiles: true }),
      closeCommandCenter: () => set({ isCommandCenterOpen: false, isManagingProfiles: false }),
      toggleCommandCenter: () => {
        const current = get().isCommandCenterOpen;
        set({ isCommandCenterOpen: !current, isManagingProfiles: !current });
      },
      setIsSwitchingProfile: (switching) => set({ isSwitchingProfile: switching }),
      setIsCreatingProfile: (creating) => set({ isCreatingProfile: creating }),

      // Actions - Notifications
      addLocalNotification: (notification) => {
        const current = get().localNotifications;
        // Keep max 50 notifications in memory
        const updated = [notification, ...current].slice(0, 50);
        set({ localNotifications: updated, showNotificationBadge: true });
      },
      removeLocalNotification: (id) => {
        const current = get().localNotifications;
        set({ localNotifications: current.filter((n) => n.id !== id) });
      },
      clearLocalNotifications: () => set({ localNotifications: [] }),
      setShowNotificationBadge: (show) => set({ showNotificationBadge: show }),

      // Actions - Filters
      setShowOnlyWithOpenTrades: (show) => set({ showOnlyWithOpenTrades: show }),
      setSearchQuery: (query) => set({ searchQuery: query }),

      // Actions - View
      setViewMode: (mode) => set({ viewMode: mode }),
      setSortBy: (sortBy) => set({ sortBy }),
      setSortOrder: (order) => set({ sortOrder: order }),

      // Actions - Trades Panel
      openTradesPanel: () => set({ isTradesPanelOpen: true }),
      closeTradesPanel: () => set({ isTradesPanelOpen: false, selectedPositionIds: [] }),
      toggleTradesPanel: () => {
        const current = get().isTradesPanelOpen;
        if (current) {
          set({ isTradesPanelOpen: false, selectedPositionIds: [] });
        } else {
          set({ isTradesPanelOpen: true });
        }
      },
      selectPosition: (positionId) => {
        const current = get().selectedPositionIds;
        if (!current.includes(positionId)) {
          set({ selectedPositionIds: [...current, positionId] });
        }
      },
      deselectPosition: (positionId) => {
        const current = get().selectedPositionIds;
        set({ selectedPositionIds: current.filter((id) => id !== positionId) });
      },
      togglePositionSelection: (positionId) => {
        const current = get().selectedPositionIds;
        if (current.includes(positionId)) {
          set({ selectedPositionIds: current.filter((id) => id !== positionId) });
        } else {
          set({ selectedPositionIds: [...current, positionId] });
        }
      },
      selectAllPositions: (positionIds) => set({ selectedPositionIds: positionIds }),
      clearPositionSelection: () => set({ selectedPositionIds: [] }),

      setIsManagingProfiles: (managing) => {
        set({ isManagingProfiles: managing, isCommandCenterOpen: managing });
      },
    }),
    {
      name: "unified-profile-store",
      version: 1,
      partialize: (state) => ({
        activeProfileId: state.activeProfileId,
        activeTradingProfileId: state.activeTradingProfileId,
        selectedProfileId: state.selectedProfileId,
        viewMode: state.viewMode,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
      }),
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;

        if (version === 0) {
          // Migrate from legacy stores on first load after deploy.
          // Old keys: "profile-store" (v1 broker profiles), "trading-profile-store" (v2 trading profiles)
          if (typeof window !== "undefined") {
            try {
              const oldProfile = localStorage.getItem("profile-store");
              if (oldProfile) {
                const parsed = JSON.parse(oldProfile)?.state;
                if (parsed?.selectedProfileId != null) {
                  state.selectedProfileId = parsed.selectedProfileId;
                }
                if (parsed?.isManagingProfiles != null) {
                  state.isManagingProfiles = parsed.isManagingProfiles;
                }
                localStorage.removeItem("profile-store");
              }

              const oldTrading = localStorage.getItem("trading-profile-store");
              if (oldTrading) {
                const parsed = JSON.parse(oldTrading)?.state;
                if (parsed?.activeProfileId != null) {
                  state.activeProfileId = parsed.activeProfileId;
                  state.activeTradingProfileId = parsed.activeProfileId;
                }
                if (parsed?.activeTradingProfileId != null) {
                  state.activeProfileId = parsed.activeTradingProfileId;
                  state.activeTradingProfileId = parsed.activeTradingProfileId;
                }
                localStorage.removeItem("trading-profile-store");
              }
            } catch {
              // Corrupted localStorage — proceed with defaults
            }
          }
        }

        return state;
      },
    }
  )
);

// =============================================
// SELECTORS (for performance)
// =============================================

/**
 * Get command center open state
 */
export const useIsCommandCenterOpen = () =>
  useUnifiedProfileStore((state) => state.isCommandCenterOpen);

/**
 * Get active profile ID
 */
export const useActiveProfileId = () =>
  useUnifiedProfileStore((state) => state.activeProfileId);

/**
 * Get local notifications
 */
export const useLocalNotifications = () =>
  useUnifiedProfileStore((state) => state.localNotifications);

/**
 * Get notification badge visibility
 */
export const useShowNotificationBadge = () =>
  useUnifiedProfileStore((state) => state.showNotificationBadge);

/**
 * Get selected position IDs
 */
export const useSelectedPositionIds = () =>
  useUnifiedProfileStore((state) => state.selectedPositionIds);

/**
 * Get filter state
 */
export const useProfileFilters = () =>
  useUnifiedProfileStore(
    // zustand v5: getSnapshot is uncached, so a selector returning a fresh
    // object literal infinite-loops React ("Maximum update depth exceeded").
    // useShallow returns the previous reference while shallow-equal.
    useShallow((state) => ({
      showOnlyWithOpenTrades: state.showOnlyWithOpenTrades,
      searchQuery: state.searchQuery,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
    })),
  );

/**
 * Get trades panel state
 */
export const useTradesPanelState = () =>
  useUnifiedProfileStore(
    // zustand v5: fresh-object selector loops without useShallow (see above).
    useShallow((state) => ({
      isOpen: state.isTradesPanelOpen,
      selectedPositionIds: state.selectedPositionIds,
    })),
  );

// =============================================
// LEGACY SELECTORS (from use-profile-store.ts)
// =============================================

export const useIsManagingProfiles = () =>
  useUnifiedProfileStore((state) => state.isManagingProfiles);

export const useSelectedProfileId = () =>
  useUnifiedProfileStore((state) => state.selectedProfileId);

export const useSetIsManagingProfiles = () =>
  useUnifiedProfileStore((state) => state.setIsManagingProfiles);

export const useSetSelectedProfileId = () =>
  useUnifiedProfileStore((state) => state.setSelectedProfileId);

export const useIsSwitchingProfile = () =>
  useUnifiedProfileStore((state) => state.isSwitchingProfile);

export const useSetIsSwitchingProfile = () =>
  useUnifiedProfileStore((state) => state.setIsSwitchingProfile);

// =============================================
// ACTIONS (standalone for use outside React)
// =============================================

export const unifiedProfileActions = {
  openCommandCenter: () => useUnifiedProfileStore.getState().openCommandCenter(),
  closeCommandCenter: () => useUnifiedProfileStore.getState().closeCommandCenter(),
  toggleCommandCenter: () => useUnifiedProfileStore.getState().toggleCommandCenter(),
  setActiveProfileId: (id: string | null) =>
    useUnifiedProfileStore.getState().setActiveProfileId(id),
  addNotification: (notification: ProfileNotification) =>
    useUnifiedProfileStore.getState().addLocalNotification(notification),
  openTradesPanel: () => useUnifiedProfileStore.getState().openTradesPanel(),
  closeTradesPanel: () => useUnifiedProfileStore.getState().closeTradesPanel(),
};


